import { useEffect, useMemo, useRef } from 'react';
import { primeSpotifyPlayer } from '../../lib/spotifyPlayerPool';
import { useNowPlaying } from './NowPlayingBar'; // the sheet's player switches while the double-tap wait runs
import VoterAvatarStack from './VoterAvatarStack';
import {
  PX_PER_HOUR,
  groupByStage,
  hourLabels,
  makeScheduleLayout,
  nowGridHour,
  scheduleBounds,
  scheduleRowsForDay,
  scheduleSpans,
} from '../../utils/scheduleGrid';
import { formatSetTimeRange, rowSizeForArtist, voteKey } from '../../utils/lineupFormat';

const STAGE_HEADER_OFFSET = 26;
const MIN_BLOCK_HEIGHT = 44;
const DOUBLE_TAP_MS = 260;
const STAGE_GRID_BOTTOM_PAD = 24;

/**
 * Horizontally-scrollable stage columns with a pinned time rail.
 * Reversed axis: top = latest end time, bottom = earliest start time.
 */
export default function LineupScheduleGrid({
  lineup = [],
  day = null,
  selectedKeys,
  votersByArtist = {},
  groupSize = null,
  onToggle,
  onPress,
  readOnly = false,
  /** Sets page: double-tap a block to pick or unpick it; a single tap still opens it (operator, 2026-09-23). */
  onDoubleTap,
  /** Stages with artists but no days or times ([{ stage, rows }], Head Trip's Sunflower Sound System): a column each,
   *  one panel the height of the day holding its artists - on all day, not at a time (Sam, 2026-10-07). */
  allDayStages = [],
}) {
  const nowPlaying = useNowPlaying();
  const rows = useMemo(() => scheduleRowsForDay(lineup, day), [lineup, day]);
  const stages = useMemo(() => groupByStage(rows), [rows]);
  // Grid hours: a set past midnight counts on from 24, so 23:00-01:00 is drawn as 23-25.
  const spans = useMemo(() => scheduleSpans(rows), [rows]);
  const { dayStart, dayEnd, gridHeight } = useMemo(
    () => scheduleBounds(rows, spans),
    [rows, spans],
  );
  const { toTop } = useMemo(() => makeScheduleLayout(dayEnd), [dayEnd]);
  const labels = useMemo(() => hourLabels(dayStart, dayEnd), [dayStart, dayEnd]);

  const nowHour = nowGridHour(day);
  const showNowLine = nowHour != null && nowHour >= dayStart && nowHour <= dayEnd;

  // One tap opens the artist, two taps pick: the single tap waits DOUBLE_TAP_MS to see whether a second one follows.
  const tapRef = useRef({ key: null, at: 0, timer: 0 });
  useEffect(() => () => window.clearTimeout(tapRef.current.timer), []);
  const handleTap = (key, row) => {
    if (typeof onDoubleTap !== 'function') {
      onPress(row);
      return;
    }
    const now = Date.now();
    const t = tapRef.current;
    if (t.key === key && now - t.at < DOUBLE_TAP_MS) {
      window.clearTimeout(t.timer);
      tapRef.current = { key: null, at: 0, timer: 0 };
      onDoubleTap(row.artist_id, row.performance_day);
      return;
    }
    window.clearTimeout(t.timer);
    tapRef.current = { key, at: now, timer: window.setTimeout(() => onPress(row), DOUBLE_TAP_MS) };
  };

  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-ink/60">No scheduled sets for this day.</p>;
  }

  const renderBlockContent = (row, { isStacked, height = 0 }) => {
    const playing =
      !nowPlaying.paused && nowPlaying.key === voteKey(row.artist_id, row.performance_day);
    const isHeadliner = rowSizeForArtist(row) === 'headliner';
    const timeRange = formatSetTimeRange([row]);
    const votedCount = (votersByArtist[row.artist_id] || []).length;
    const hasVotePill = votedCount > 0 && groupSize != null;

    const nameEl = (
      <span
        className={
          isHeadliner
            ? 'schedule-grid__block-name schedule-grid__block-name--headliner'
            : 'schedule-grid__block-name schedule-grid__block-name--support'
        }
      >
        {row.artist_name}
        {playing && (
          <span className="play-eq" aria-label="Playing">
            <i />
            <i />
            <i />
          </span>
        )}
      </span>
    );

    // Two faces in the pill (Sam's A, shared with the Sets rows) where the block is tall enough that the wider pill clears
    // the set time (70 px: Dog Blood, 75 px, did; Robyn, 60, did not); shorter blocks keep the count.
    const showFaces = isStacked && height >= 70;
    const votePill = hasVotePill ? (
      <span
        className={`schedule-grid__vote-pill ${
          isStacked ? 'schedule-grid__vote-pill--stacked' : 'schedule-grid__vote-pill--inline'
        }${showFaces ? ' schedule-grid__vote-pill--faces' : ''}`}
      >
        {showFaces && (
          <VoterAvatarStack
            people={votersByArtist[row.artist_id]}
            max={2}
            size={14}
            overlap={-2}
            showOverflow={false}
            className="voter-avatar-stack--ring-only"
          />
        )}
        {`${votedCount}/${groupSize}`}
      </span>
    ) : null;

    if (isStacked) {
      return (
        <>
          {nameEl}
          {timeRange && <span className="schedule-grid__block-time">{timeRange}</span>}
          {votePill}
        </>
      );
    }

    return (
      <div className="schedule-grid__block-row">
        {nameEl}
        {votePill}
      </div>
    );
  };

  /** renderBlock, for one stage column. `ceilingHour` is the start of the next set in that
   *  column — the line this block must not cross. */
  const renderBlock = (row, ceilingHour) => {
    const span = spans.get(row);
    if (!span) return null;
    const { start, end } = span;

    const key = voteKey(row.artist_id, row.performance_day);
    const selected = selectedKeys?.has(key) ?? false;
    const votable = !readOnly && typeof onToggle === 'function';
    const pressable = typeof onPress === 'function';
    const durationHeight = (end - start) * PX_PER_HOUR;
    // A short set is given a minimum height so its name and time still fit, and the block
    // grows from its start time toward later times. On this reversed axis that is UPWARD,
    // straight into the next set: Punkybutter (12:00-12:30, 30px at 60px/hour) was forced
    // to 44px and covered 14px of GREG 99 at 12:30 (operator, 2026-09-24). So the minimum
    // only applies as far as the next set's start — a 30-minute set back-to-back with
    // another one stays 30 minutes tall and renders compact.
    const headroom = ceilingHour == null ? Infinity : (ceilingHour - start) * PX_PER_HOUR;
    const renderedHeight = Math.max(durationHeight, Math.min(MIN_BLOCK_HEIGHT, headroom));
    const isStacked = renderedHeight >= MIN_BLOCK_HEIGHT;
    const votedCount = (votersByArtist[row.artist_id] || []).length;
    const hasVotePill = votedCount > 0 && groupSize != null;

    const blockClass = [
      'schedule-grid__block',
      selected ? 'schedule-grid__block--selected' : 'schedule-grid__block--unselected',
      isStacked ? 'schedule-grid__block--stacked' : 'schedule-grid__block--compact',
      hasVotePill ? 'schedule-grid__block--has-vote-pill' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Anchor the block bottom to set start time so min-height grows upward,
    // avoiding overlap/clipping on the block below.
    const style = {
      top: toTop(start) - renderedHeight,
      height: renderedHeight,
    };

    const content = renderBlockContent(row, { isStacked, height: renderedHeight });

    if (votable) {
      return (
        <button
          key={key}
          type="button"
          className={blockClass}
          style={style}
          aria-pressed={selected}
          onClick={() => onToggle(row.artist_id, row.performance_day)}
        >
          {content}
        </button>
      );
    }

    if (pressable) {
      return (
        <button
          key={key}
          type="button"
          className={blockClass}
          style={style}
          onPointerDown={() =>
            primeSpotifyPlayer(
              (row.members?.find((m) => m.spotify_artist_id) ?? row).spotify_artist_id,
            )
          }
          onClick={() => handleTap(key, row)}
        >
          {content}
        </button>
      );
    }

    return (
      <div key={key} className={blockClass} style={style}>
        {content}
      </div>
    );
  };

  const stageGridHeight = gridHeight + STAGE_GRID_BOTTOM_PAD;

  return (
    <div className="schedule-grid">
      <div className="schedule-grid__body">
        <div className="schedule-grid__rail-wrap">
          <div className="schedule-grid__rail-spacer" aria-hidden />
          <div className="schedule-grid__rail" style={{ height: stageGridHeight }}>
            {labels.map(({ hour, label }) => (
              <span
                key={hour}
                className="schedule-grid__rail-label"
                style={{ top: toTop(hour), transform: 'translateY(-50%)' }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="schedule-grid__stages-scroll scrollbar-hide">
          <div className="schedule-grid__stages-inner">
            {[...stages, ...allDayStages.map((g) => ({ ...g, allDay: true }))].map(
              ({ stage, rows: stageRows, allDay }) => {
                if (allDay) {
                  return (
                    <div key={`allday|${stage}`} className="schedule-grid__stage-col">
                      <p className="schedule-grid__stage-name">{stage}</p>
                      <div
                        className="schedule-grid__stage-grid"
                        style={{ height: stageGridHeight }}
                      >
                        <div
                          className="schedule-grid__all-day"
                          style={{ top: 0, height: gridHeight }}
                        >
                          <span className="schedule-grid__all-day-label">All weekend</span>
                          {stageRows.map((row) => {
                            const key = voteKey(row.artist_id, row.performance_day);
                            const selected = selectedKeys?.has(key) ?? false;
                            const votedCount = (votersByArtist[row.artist_id] || []).length;
                            const cls = `schedule-grid__all-day-item ${selected ? 'schedule-grid__block--selected' : 'schedule-grid__block--unselected'}`;
                            const body = (
                              <>
                                <span className="schedule-grid__block-name schedule-grid__block-name--support">
                                  {row.artist_name}
                                </span>
                                {votedCount > 0 && groupSize != null && (
                                  <span className="schedule-grid__vote-pill schedule-grid__vote-pill--inline">
                                    {votedCount}/{groupSize}
                                  </span>
                                )}
                              </>
                            );
                            return typeof onPress === 'function' ? (
                              <button
                                key={key}
                                type="button"
                                className={cls}
                                onPointerDown={() =>
                                  primeSpotifyPlayer(
                                    (row.members?.find((m) => m.spotify_artist_id) ?? row)
                                      .spotify_artist_id,
                                  )
                                }
                                onClick={() => handleTap(key, row)}
                              >
                                {body}
                              </button>
                            ) : (
                              <div key={key} className={cls}>
                                {body}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={stage} className="schedule-grid__stage-col">
                    <p className="schedule-grid__stage-name">{stage}</p>
                    <div className="schedule-grid__stage-grid" style={{ height: stageGridHeight }}>
                      {/* Each block's ceiling is the next set's start in this column, so a
                        short block can expand into a gap but never onto its neighbour. */}
                      {(() => {
                        const ordered = [...stageRows].sort(
                          (a, b) => (spans.get(a)?.start ?? 0) - (spans.get(b)?.start ?? 0),
                        );
                        return ordered.map((row, i) => {
                          const starts = ordered
                            .slice(i + 1)
                            .map((r) => spans.get(r)?.start)
                            .filter((h) => h != null);
                          return renderBlock(row, starts.length ? Math.min(...starts) : null);
                        });
                      })()}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>

      {showNowLine && (
        <div
          className="schedule-grid__now-line"
          style={{ top: STAGE_HEADER_OFFSET + toTop(nowHour) }}
          aria-hidden
        >
          <span className="schedule-grid__now-dot" />
        </div>
      )}
    </div>
  );
}
